/**
 * Candidate Review Stage Component
 * Third stage where user evaluates candidates
 */

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Separator } from '../ui/separator';
import VoiceInput from '../VoiceInput';
import CandidateInquiryChat from './CandidateInquiryChat';
import {
    ArrowRight,
    Users,
    CheckCircle,
    Loader2,
    ChevronRight,
    ChevronLeft,
    GraduationCap,
    ThumbsUp,
    ThumbsDown,
    HelpCircle,
    Mic,
    Briefcase,
    Target,
    Clock,
    DollarSign,
    Award,
    MapPin
} from 'lucide-react';

const CandidateReviewStage = ({
    candidates,
    currentIndex,
    setCurrentIndex,
    currentCandidate,
    verdicts,
    setVerdicts,
    reasonings,
    setReasonings,
    onSubmitEvaluation,
    evaluationProgress,
    onContinue,
    loading,
    jobBrief,
    api,
    sessionId
}) => {
    const evaluatedCount = Object.keys(verdicts).filter(id => verdicts[id]).length;
    const minRequired = evaluationProgress?.min_required || 5;
    const canProceed = evaluatedCount >= minRequired;

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            <Users className="h-5 w-5 text-emerald-500" />
                            Candidate Review
                        </CardTitle>
                        <CardDescription>
                            Evaluate each candidate&apos;s fit for: <strong>{jobBrief?.title}</strong>
                        </CardDescription>
                    </div>
                    <Badge variant="outline">
                        {currentIndex + 1} / {candidates.length}
                    </Badge>
                </div>
                <Progress value={(evaluatedCount / candidates.length) * 100} className="h-2" />
                <p className="text-xs text-muted-foreground">
                    {evaluatedCount} evaluated • {minRequired} required to continue
                </p>
            </CardHeader>
            <CardContent className="space-y-6">
                {currentCandidate ? (
                    <>
                        {/* Candidate Header */}
                        <div className="p-4 rounded-xl bg-gradient-to-r from-primary/5 to-secondary/5 border">
                            <div className="flex items-start justify-between">
                                <div>
                                    <h3 className="text-xl font-bold">{currentCandidate.name}</h3>
                                    <p className="text-muted-foreground">{currentCandidate.linkedin_headline}</p>
                                </div>
                                {verdicts[currentCandidate.id] && (
                                    <Badge variant={verdicts[currentCandidate.id] === 'good_fit' ? 'default' : 'destructive'}>
                                        {verdicts[currentCandidate.id] === 'good_fit' ? 'Good Fit' : 
                                         verdicts[currentCandidate.id] === 'partial_fit' ? 'Partial Fit' : 'Not a Fit'}
                                    </Badge>
                                )}
                            </div>
                        </div>

                        {/* Candidate Details */}
                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="p-3 rounded-lg bg-muted/30">
                                <p className="text-xs text-muted-foreground mb-1">Current Role</p>
                                <p className="font-medium">{currentCandidate.current_title}</p>
                                <p className="text-sm text-muted-foreground">{currentCandidate.current_company}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-muted/30">
                                <p className="text-xs text-muted-foreground mb-1">Experience</p>
                                <p className="font-medium">{currentCandidate.total_experience_years} years</p>
                                <p className="text-sm text-muted-foreground">{currentCandidate.current_industry}</p>
                            </div>
                        </div>

                        {/* Profile Indicators */}
                        <div className="flex flex-wrap gap-2">
                            <Badge variant="outline" className="capitalize">
                                {currentCandidate.job_stability?.replace('-', ' ')}
                            </Badge>
                            <Badge variant="outline" className="capitalize">
                                {currentCandidate.career_progression?.replace('-', ' ')}
                            </Badge>
                            <Badge variant="outline" className="capitalize">
                                {currentCandidate.industry_relevance?.replace('-', ' ')}
                            </Badge>
                        </div>

                        {/* Skills */}
                        <div>
                            <h4 className="font-semibold mb-2">Skills</h4>
                            <div className="flex flex-wrap gap-1">
                                {currentCandidate.skills?.map((skill, idx) => (
                                    <Badge key={idx} variant="secondary" className="text-xs">
                                        {skill}
                                    </Badge>
                                ))}
                            </div>
                        </div>

                        {/* Work History */}
                        {currentCandidate.work_history?.length > 0 && (
                            <div>
                                <h4 className="font-semibold mb-2 flex items-center gap-2">
                                    <Briefcase className="h-4 w-4" />
                                    Work History
                                </h4>
                                <div className="space-y-3">
                                    {currentCandidate.work_history.map((work, idx) => (
                                        <div key={idx} className="p-3 rounded-lg border">
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <p className="font-medium">{work.title}</p>
                                                    <p className="text-sm text-muted-foreground">
                                                        {work.company} • {work.company_type}
                                                    </p>
                                                </div>
                                                <Badge variant="outline" className="text-xs">
                                                    {/* Format duration - handle both integer and decimal values */}
                                                    {(() => {
                                                        const duration = work.duration_years || 0;
                                                        // Detect likely decimal errors (e.g., 15 should be 1.5 for junior roles)
                                                        const isLikelyError = duration >= 10 && 
                                                            (work.title?.toLowerCase().includes('junior') || 
                                                             work.title?.toLowerCase().includes('intern') ||
                                                             work.title?.toLowerCase().includes('associate'));
                                                        const displayDuration = isLikelyError ? (duration / 10).toFixed(1) : 
                                                            Number.isInteger(duration) ? duration : duration.toFixed(1);
                                                        return `${displayDuration}y`;
                                                    })()}
                                                </Badge>
                                            </div>
                                            
                                            {/* Key Projects */}
                                            {work.key_projects?.length > 0 && (
                                                <div className="mt-2">
                                                    <p className="text-xs font-medium text-muted-foreground">Key Projects:</p>
                                                    <ul className="space-y-1 text-xs text-muted-foreground">
                                                        {work.key_projects.slice(0, 2).map((proj, pidx) => (
                                                            <li key={pidx}>• {proj}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                            
                                            {/* Achievements */}
                                            {work.achievements?.length > 0 && (
                                                <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                                                    {work.achievements.slice(0, 2).map((ach, aidx) => (
                                                        <li key={aidx}>• {ach}</li>
                                                    ))}
                                                </ul>
                                            )}
                                            
                                            {/* Reason for Leaving */}
                                            {work.reason_for_leaving && (
                                                <p className="mt-2 text-xs text-amber-600 bg-amber-500/10 px-2 py-1 rounded inline-block">
                                                    Left: {work.reason_for_leaving}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Education */}
                        <div>
                            <h4 className="font-semibold mb-2">Education</h4>
                            <div className="space-y-2">
                                {currentCandidate.education?.map((edu, idx) => (
                                    <div key={idx} className="flex items-center gap-2 text-sm">
                                        <GraduationCap className="h-4 w-4 text-muted-foreground" />
                                        {edu.degree} - {edu.institution} ({edu.year})
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Enhanced Profile Details */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {currentCandidate.salary_expectation && (
                                <div className="p-2 rounded-lg bg-muted/30">
                                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                                        <DollarSign className="h-3 w-3" /> Expected Salary
                                    </p>
                                    <p className="text-sm font-medium">{currentCandidate.salary_expectation}</p>
                                </div>
                            )}
                            {currentCandidate.notice_period && (
                                <div className="p-2 rounded-lg bg-muted/30">
                                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                                        <Clock className="h-3 w-3" /> Notice Period
                                    </p>
                                    <p className="text-sm font-medium">{currentCandidate.notice_period}</p>
                                </div>
                            )}
                            {currentCandidate.location && (
                                <div className="p-2 rounded-lg bg-muted/30">
                                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                                        <MapPin className="h-3 w-3" /> Location
                                    </p>
                                    <p className="text-sm font-medium">{currentCandidate.location}</p>
                                    {currentCandidate.willing_to_relocate !== undefined && (
                                        <p className="text-xs text-muted-foreground">
                                            {currentCandidate.willing_to_relocate ? 'Open to relocate' : 'Not willing to relocate'}
                                        </p>
                                    )}
                                </div>
                            )}
                            {currentCandidate.career_goals && (
                                <div className="p-2 rounded-lg bg-muted/30">
                                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                                        <Target className="h-3 w-3" /> Career Goal
                                    </p>
                                    <p className="text-sm font-medium line-clamp-2">{currentCandidate.career_goals}</p>
                                </div>
                            )}
                        </div>

                        {/* Certifications */}
                        {currentCandidate.certifications?.length > 0 && (
                            <div>
                                <h4 className="font-semibold mb-2 flex items-center gap-2">
                                    <Award className="h-4 w-4 text-amber-500" />
                                    Certifications
                                </h4>
                                <div className="flex flex-wrap gap-1">
                                    {currentCandidate.certifications.map((cert, idx) => (
                                        <Badge key={idx} variant="outline" className="text-xs bg-amber-500/10">
                                            {cert}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Key Achievements */}
                        {currentCandidate.key_achievements?.length > 0 && (
                            <div>
                                <h4 className="font-semibold mb-2">Key Achievements</h4>
                                <div className="space-y-2">
                                    {currentCandidate.key_achievements.slice(0, 3).map((ach, idx) => (
                                        <div key={idx} className="p-2 rounded-lg border bg-gradient-to-r from-emerald-500/5 to-transparent">
                                            <p className="text-sm font-medium">{ach.title}</p>
                                            {ach.description && (
                                                <p className="text-xs text-muted-foreground">{ach.description}</p>
                                            )}
                                            {ach.impact && (
                                                <p className="text-xs text-emerald-600 mt-1">Impact: {ach.impact}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Candidate Inquiry Chat */}
                        {sessionId && (
                            <CandidateInquiryChat
                                sessionId={sessionId}
                                candidate={currentCandidate}
                                api={api}
                            />
                        )}

                        <Separator />

                        {/* Your Evaluation */}
                        <div className="space-y-4">
                            <h4 className="font-semibold">Your Evaluation</h4>
                            
                            {/* Verdict Selection */}
                            <RadioGroup
                                value={verdicts[currentCandidate.id] || ''}
                                onValueChange={(val) => setVerdicts({ ...verdicts, [currentCandidate.id]: val })}
                            >
                                <div className="grid grid-cols-3 gap-3">
                                    <div className={`flex items-center space-x-2 p-3 rounded-lg border cursor-pointer transition-all ${
                                        verdicts[currentCandidate.id] === 'good_fit' ? 'bg-emerald-500/10 border-emerald-500' : 'hover:bg-muted/50'
                                    }`}>
                                        <RadioGroupItem value="good_fit" id="good_fit" />
                                        <Label htmlFor="good_fit" className="flex items-center gap-2 cursor-pointer">
                                            <ThumbsUp className="h-4 w-4 text-emerald-500" />
                                            Good Fit
                                        </Label>
                                    </div>
                                    <div className={`flex items-center space-x-2 p-3 rounded-lg border cursor-pointer transition-all ${
                                        verdicts[currentCandidate.id] === 'partial_fit' ? 'bg-amber-500/10 border-amber-500' : 'hover:bg-muted/50'
                                    }`}>
                                        <RadioGroupItem value="partial_fit" id="partial_fit" />
                                        <Label htmlFor="partial_fit" className="flex items-center gap-2 cursor-pointer">
                                            <HelpCircle className="h-4 w-4 text-amber-500" />
                                            Partial Fit
                                        </Label>
                                    </div>
                                    <div className={`flex items-center space-x-2 p-3 rounded-lg border cursor-pointer transition-all ${
                                        verdicts[currentCandidate.id] === 'not_a_fit' ? 'bg-red-500/10 border-red-500' : 'hover:bg-muted/50'
                                    }`}>
                                        <RadioGroupItem value="not_a_fit" id="not_a_fit" />
                                        <Label htmlFor="not_a_fit" className="flex items-center gap-2 cursor-pointer">
                                            <ThumbsDown className="h-4 w-4 text-red-500" />
                                            Not a Fit
                                        </Label>
                                    </div>
                                </div>
                            </RadioGroup>

                            {/* Reasoning - Voice Input */}
                            <div className="p-4 rounded-xl border bg-muted/20">
                                <Label className="text-sm font-medium mb-3 block flex items-center gap-2">
                                    <Mic className="h-4 w-4" />
                                    Speak Your Evaluation
                                </Label>
                                <VoiceInput
                                    api={api}
                                    value={reasonings[currentCandidate.id] || ''}
                                    onTranscript={(text) => setReasonings({ ...reasonings, [currentCandidate.id]: text })}
                                    placeholder="Explain why this candidate is or isn't a good fit. Consider industry, company background, experience, and red flags..."
                                    showTranscript={true}
                                />
                            </div>

                            {/* Submit Evaluation */}
                            <Button
                                onClick={() => {
                                    onSubmitEvaluation(currentCandidate.id);
                                }}
                                disabled={loading || !verdicts[currentCandidate.id] || !reasonings[currentCandidate.id]?.trim()}
                                className={`w-full ${loading ? 'opacity-70' : ''}`}
                                variant="secondary"
                                data-testid="submit-evaluation-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Submitting... Please wait
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="h-4 w-4 mr-2" />
                                        Submit Evaluation
                                    </>
                                )}
                            </Button>
                            
                            {/* Show button state for debugging */}
                            {(!verdicts[currentCandidate.id] || !reasonings[currentCandidate.id]?.trim()) && (
                                <p className="text-xs text-muted-foreground text-center mt-1">
                                    {!verdicts[currentCandidate.id] && "Please select a fit rating. "}
                                    {!reasonings[currentCandidate.id]?.trim() && "Please provide your reasoning."}
                                </p>
                            )}
                        </div>

                        {/* Navigation */}
                        <div className="flex items-center justify-between pt-4 border-t">
                            <Button
                                variant="outline"
                                onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
                                disabled={currentIndex === 0}
                            >
                                <ChevronLeft className="h-4 w-4 mr-1" />
                                Previous
                            </Button>
                            
                            <div className="flex gap-1">
                                {candidates.map((_, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setCurrentIndex(idx)}
                                        className={`w-2 h-2 rounded-full transition-all ${
                                            idx === currentIndex ? 'bg-primary w-4' : 
                                            verdicts[candidates[idx]?.id] ? 'bg-emerald-500' : 'bg-muted'
                                        }`}
                                    />
                                ))}
                            </div>
                            
                            <Button
                                variant="outline"
                                onClick={() => setCurrentIndex(Math.min(candidates.length - 1, currentIndex + 1))}
                                disabled={currentIndex === candidates.length - 1}
                            >
                                Next
                                <ChevronRight className="h-4 w-4 ml-1" />
                            </Button>
                        </div>
                    </>
                ) : (
                    <p className="text-center text-muted-foreground py-8">No candidates to review</p>
                )}

                {/* Continue to AI Discussion */}
                {canProceed && (
                    <div className="pt-4 border-t">
                        <Button 
                            onClick={onContinue} 
                            disabled={loading}
                            className="w-full"
                            data-testid="continue-to-discussion"
                        >
                            {loading ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <>
                                    Continue to AI Discussion
                                    <ArrowRight className="h-4 w-4 ml-2" />
                                </>
                            )}
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default CandidateReviewStage;
